import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export async function generateConciliationExcel({
  initialDate,
  finalDate,
  receiptData,
}) {
  const workbook = new ExcelJS.Workbook();

  const titleStyle = {
    font: { bold: true, size: 14 },
    alignment: { vertical: "middle", horizontal: "center" },
  };

  const headerStyle = {
    font: { bold: true, color: { argb: "FFFFFFFF" } },
    fill: { type: "pattern", pattern: "solid", fgColor: { argb: "2C2E65" } },
    alignment: { horizontal: "center", vertical: "middle", wrapText: true },
    border: {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    },
  };

  const cellBorder = {
    top: { style: "thin" },
    left: { style: "thin" },
    bottom: { style: "thin" },
    right: { style: "thin" },
  };

  const moneyFormat = "R$ #,##0.00";
  const dateFormat = "dd/mm/yyyy";

  const dictionary = {
    gross_amount: "Valor Bruto",
    not_conciliated: "Não Conciliado",
    ecommerce: "E-commerce",
    pos: "POS",
    tef: "TEF",
    quantity_not_conciliated: "Quantidade Não Conciliada",
    ec: "EC",
    nsu: "NSU",
    transaction_date: "Data da Transação",
    platform_type: "Plataforma",
    installment_quantity: "Quantidade de Parcelas",
    brand: "Bandeira",
    status: "Status",
    processing: "Processando",
    pending: "Pendente",
    gross_amount_not_in_veripag: "Não consta na Veripag",
    installment_number: "Número da Parcela",
    total: "Total",
    file_date: "Data do Arquivo",
    is_conciliated: "Está Conciliado",
    date: "Data",
    register: "Cadastro",
    establishment: "Estabelecimento",
    affiliation: "Afiliação",
    quantity: "Quantidade",
    tid: "TID",
    devolution_amount: "Valor de Devolução"
  };

  const moneyFields = [
    "gross_amount",
    "net_amount",
    "amount_paid",
    "amount_expected",
    "fees",
    "not_conciliated",
    "ecommerce",
    "pos",
    "tef",
    "gross_amount_not_in_veripag",
    "processing",
    "pending",
    "total",
    "devolution_amount"
  ];

  function translate(key) {
    return dictionary[key] || key;
  }

  function yieldToBrowser() {
    return new Promise(resolve => setTimeout(resolve, 0));
  }

  function addTitle(sheet, title) {
    sheet.mergeCells("A1:B1");
    const cell = sheet.getCell("A1");
    cell.value = title;
    cell.style = titleStyle;
  }

  function addPixResume(sheet, pix, startRow = 4) {
    let row = startRow;

    const header = sheet.getRow(row);
    header.values = ["Tipo", "Veripag", "Itaú", "GSurf"];
    header.eachCell(c => (c.style = headerStyle));

    row++;

    const grossRow = sheet.getRow(row);
    grossRow.values = [
      "Total Bruto",
      pix.veripag?.gross_amount || 0,
      pix.itau?.gross_amount || 0,
      pix.gsurf?.gross_amount || 0,
    ];

    row++;

    const notConciliatedRow = sheet.getRow(row);
    notConciliatedRow.values = [
      "Não Conciliado",
      pix.veripag?.not_conciliated || 0,
      pix.itau?.not_conciliated || 0,
      pix.gsurf?.not_conciliated || 0,
    ];
    
    row++; 

    const quantityNotConciliatedRow = sheet.getRow(row);
    quantityNotConciliatedRow.values = [
      "Qtd. Não Conciliado",
      pix.veripag?.quantity_not_conciliated || 0,
      pix.itau?.quantity_not_conciliated || 0,
      pix.gsurf?.quantity_not_conciliated || 0,
    ];

    [grossRow, notConciliatedRow].forEach(r => {
      r.eachCell(cell => {
        cell.border = cellBorder;
        cell.alignment = { horizontal: "center", vertical: "middle" };
        if (typeof cell.value === "number") {
          cell.numFmt = moneyFormat;
        }
      });
    });

    return row + 2;
  }
  function addResume(sheet, data, startRow = 3) {
    let row = startRow;

    sheet.getRow(row).values = ["Descrição", "Valor"];
    sheet.getRow(row).eachCell(c => (c.style = headerStyle));

    row++;

    Object.entries(data).forEach(([key, value]) => {

      if (Array.isArray(value)) return;

      const r = sheet.getRow(row);
      r.getCell(1).value = translate(key);
      r.getCell(2).value = value;

      r.getCell(1).alignment = { wrapText: true, horizontal: "center", vertical: "middle" };
      r.getCell(2).alignment = { wrapText: true, horizontal: "center", vertical: "middle" };


      if (typeof value === "number" && moneyFields.includes(key)) {
        r.getCell(2).numFmt = moneyFormat;
      }

      r.getCell(1).border = cellBorder;
      r.getCell(2).border = cellBorder;

      row++;
    });

    return row + 2;
  }

  async function addTable(sheet, data, startRow, moduleName) {
    if (!Array.isArray(data) || !data.length) {
      const emptyMessage = moduleName === "Estabelecimentos"
        ? "Nenhum registro encontrado."
        : "Nenhuma transação encontrada.";

      sheet.getRow(startRow).getCell(1).value = emptyMessage;
      return;
    }

    const headers = Object.keys(data[0]);
    const headerRow = sheet.getRow(startRow);
    headerRow.values = headers.map(h => translate(h));
    headerRow.eachCell(c => (c.style = headerStyle));

    headers.forEach((header, index) => {
      sheet.getColumn(index + 1).width = header === "tid" ? 40 : 27;
      sheet.getColumn(index + 1).alignment = {
        horizontal: "center",
        vertical: "middle",
      };
    });

    startRow++;

    for (let index = 0; index < data.length; index += 1) {
      const item = data[index];
      const row = sheet.getRow(startRow);

      headers.forEach((h, idx) => {
        let value = item[h];
        const cell = row.getCell(idx + 1);

        if (h.includes("date") && value) {
          value = new Date(value);
          cell.numFmt = dateFormat;
        }

        if (typeof value === "number" && moneyFields.includes(h)) {
          cell.numFmt = moneyFormat;
        }

        cell.value = value;
      });

      startRow++;
      if ((index + 1) % 500 === 0) {
        await yieldToBrowser();
      }
    }
  }

  function formatDateBR(dateString) {
    const [year, month, day] = dateString.split("-");
    return `${day}/${month}/${year}`;
  }

  function addQueryDate(sheet, initialDate, finalDate, rowNumber = 2) {
    const row = sheet.getRow(rowNumber);
    row.getCell(1).value = `Período da Consulta: ${formatDateBR(initialDate)} até ${formatDateBR(finalDate)}`;
    sheet.mergeCells(rowNumber, 1, rowNumber, 2);
    row.getCell(1).style = {
      font: { name: "Calibri", size: 12, italic: true },
      alignment: { horizontal: "center", vertical: "middle" },
      border: {
        top: { style: "none" },
        left: { style: "none" },
        bottom: { style: "none" },
        right: { style: "none" },
      },
    };
    row.height = 18;
  }


  const modules = [
    { name: "Veripag", data: receiptData.veripag, arrayField: "transaction_not_conciliated" },
    { name: "EDI REDE", data: receiptData.rede, arrayField: "transaction_not_conciliated" },
    { name: "Processando & Pendente", data: receiptData.processing_or_pending, arrayField: "transactions_processing_or_pending" },
    { name: "Outros Status", data: receiptData.othersStatus, arrayField: "transactions" },
    { name: "Gateway GSurf", data: receiptData.gateway?.gsurf || {}, arrayField: "transaction_not_conciliated" },
    { name: "Gateway PayGo", data: receiptData.gateway?.paygo || {}, arrayField: "transaction_not_conciliated" },
    { name: "Fora de data", data: receiptData.outOfDate || {}, arrayField: "transactions" },
    { name: "Estabelecimentos", data: { transactions: receiptData.establishment || [] }, arrayField: "transactions" },
    { name: "PIX", data: receiptData.pix || {}, isPix: true },
  ];


  for (const module of modules) {
    const sheet = workbook.addWorksheet(module.name);

    const totalColumns = 2;

    for (let i = 1; i <= totalColumns; i++) {
      sheet.getColumn(i).width = 27;
    }

    sheet.mergeCells(1, 1, 1, totalColumns);
    const titleCell = sheet.getCell("A1");
    titleCell.value = `Resumo ${module.name}`;
    titleCell.style = {
      font: { bold: true, size: 14 },
      alignment: { horizontal: "center", vertical: "middle" },
    };

    sheet.mergeCells(2, 1, 2, totalColumns);
    const queryCell = sheet.getCell("A2");
    queryCell.value = `Período da Consulta: ${formatDateBR(initialDate)} até ${formatDateBR(finalDate)}`;
    queryCell.style = {
      font: { italic: true, size: 12 },
      alignment: { horizontal: "center", vertical: "middle" },
    };

    let tableStartRow;

    if (module.isPix) {
      const end = addPixResume(sheet, module.data, 4);
      tableStartRow = end + 1;
    } else if (module.name !== "Estabelecimentos") {
      const end = addResume(sheet, module.data, 4);
      tableStartRow = end + 1;
    } else {
      tableStartRow = 4;
    }

    const transactionTitleRow = sheet.getRow(tableStartRow);
    if (module.name === "Estabelecimentos") {
      transactionTitleRow.getCell(1).value = "Registros";
    } else {
      transactionTitleRow.getCell(1).value = "Transações";
    }
    sheet.mergeCells(tableStartRow, 1, tableStartRow, 2);
    transactionTitleRow.getCell(1).style = {
      font: { name: "Calibri", size: 14, bold: true },
      alignment: { horizontal: "center", vertical: "middle" },
      border: { top: { style: "none" }, left: { style: "none" }, bottom: { style: "none" }, right: { style: "none" } },
    };
    transactionTitleRow.height = 22;

    if (module.isPix) {
      let row = tableStartRow + 2;

      sheet.getRow(row).getCell(1).value = "Transações Veripag";
      row += 2;

      await addTable(
        sheet,
        module.data.veripag?.transaction_not_conciliated || [],
        row,
        "PIX Veripag"
      );

      row += (module.data.veripag?.transaction_not_conciliated?.length || 1) + 3;

      sheet.getRow(row).getCell(1).value = "Transações Itaú";
      row += 2;

      await addTable(
        sheet,
        module.data.itau?.transaction_not_conciliated || [],
        row,
        "PIX Itaú"
      );

      row += (module.data.itau?.transaction_not_conciliated?.length || 1) + 3;

      sheet.getRow(row).getCell(1).value = "Transações GSurf";
      row += 2;

      await addTable(
        sheet,
        module.data.gsurf?.transaction_not_conciliated || [],
        row,
        "PIX GSurf"
      );

    } else {
      await addTable(
        sheet,
        module.data[module.arrayField] || [],
        tableStartRow + 2,
        module.name
      );
    }

    sheet.columns.forEach(col => {
      col.alignment = { horizontal: "center", vertical: "middle" };
    });
  }


  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer]),
    `Relatorio_Conciliacao_${initialDate}_até_${finalDate}.xlsx`
  );
}
